<script lang="ts">
  import ChevronDownIcon from "../utils/assets/chevron_down.svelte";
  import SearchIcon from "../utils/assets/search_icon.svelte";
  import FlagIcon from "./FlagIcon.svelte";
  import { COUNTRY_OPTIONS } from "../utils/constants";
  import { onMount, createEventDispatcher } from "svelte";
  import { parsePhoneNumberFromString } from "libphonenumber-js";
  import type { CountryCode } from "libphonenumber-js";

  const dispatch = createEventDispatcher<{
    countryChange: { countryCode: string };
    select: void;
    validationChange: { isValid: boolean; phoneData: PhoneNumberData };
    blur: void;
  }>();

  export let phoneNumber: string = "";
  export let showValidationError: boolean = false;

  let searchQuery = "";
  let selectedCountry =
    COUNTRY_OPTIONS.find((country) => country.flag === "gb") ||
    COUNTRY_OPTIONS[0];
  let isOpen = false;
  let dropdownContainer: HTMLElement;
  let isValid = true;

  interface PhoneNumberData {
    countryCode: string;
    phoneNumber: string;
    fullNumber: string;
    isValid: boolean;
    nationalNumber?: string;
    internationalNumber?: string;
  }

  const popularCountries = ["gb", "cn", "us", "in"]
    .map((flag) => COUNTRY_OPTIONS.find((country) => country.flag === flag))
    .filter(
      (country): country is (typeof COUNTRY_OPTIONS)[0] => country !== undefined
    );

  const otherCountries = COUNTRY_OPTIONS.filter(
    (country) => !["gb", "cn", "us", "in"].includes(country.flag)
  );

  function validatePhoneNumber(phone: string, country: any): PhoneNumberData {
    if (!country) {
      return {
        countryCode: "",
        phoneNumber: phone,
        fullNumber: phone,
        isValid: false,
      };
    }

    if (!phone) {
      return {
        countryCode: country.flag.toUpperCase(),
        phoneNumber: "",
        fullNumber: "",
        isValid: false,
      };
    }

    try {
      const countryCode = country.flag.toUpperCase() as CountryCode;
      const digitsOnly = phone.replace(/\D/g, "");

      let phoneNumber = parsePhoneNumberFromString(digitsOnly, countryCode);

      if (!phoneNumber) {
        const fullNumber = `${country.code}${digitsOnly}`;
        phoneNumber = parsePhoneNumberFromString(fullNumber);
      }

      if (phoneNumber) {
        const isValid =
          phoneNumber.isValid() && phoneNumber.country === countryCode;

        return {
          countryCode: country.flag.toUpperCase(),
          phoneNumber: digitsOnly,
          fullNumber: phoneNumber.number,
          nationalNumber: phoneNumber.nationalNumber,
          internationalNumber: phoneNumber.formatInternational(),
          isValid: isValid,
        };
      }
    } catch (error) {
      console.warn("Phone validation error:", error);
    }

    const digitsOnly = phone.replace(/\D/g, "");
    return {
      countryCode: country.flag.toUpperCase(),
      phoneNumber: digitsOnly,
      fullNumber: `${country.code}${digitsOnly}`,
      isValid: false,
    };
  }

  function formatPhoneNumber(phone: string, country: any): string {
    if (!phone || !country) return phone;

    const digits = phone.replace(/\D/g, "");
    if (!digits) return "";

    // Force custom formatting for numbers longer than 11 digits
    if (digits.length > 11) {
      return formatPhoneNumberFallback(digits, country);
    }

    try {
      const countryCode = country.flag.toUpperCase() as CountryCode;

      let phoneNumber = parsePhoneNumberFromString(digits, countryCode);

      if (!phoneNumber) {
        const withDialCode = `${country.code}${digits}`;
        phoneNumber = parsePhoneNumberFromString(withDialCode);
      }

      if (phoneNumber && phoneNumber.country === countryCode) {
        let formatted = phoneNumber.formatNational();

        formatted = formatted
          .replace(new RegExp(`^\\${country.code}\\s*`), "")
          .trim();

        if (
          formatted.startsWith("0") &&
          digits.length > 0 &&
          !digits.startsWith("0")
        ) {
          formatted = formatted.substring(1);
        }

        return formatted;
      }
    } catch (error) {
      console.warn("Phone formatting error:", error);
    }

    return formatPhoneNumberFallback(digits, country);
  }

  function formatPhoneNumberFallback(digits: string, country: any): string {
    if (!digits) return digits;

    if (country.flag === "us" || country.flag === "ca") {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6)
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      if (digits.length <= 10)
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      if (digits.length <= 13)
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)} ${digits.slice(10)}`;
      if (digits.length <= 16)
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)} ${digits.slice(10, 13)} ${digits.slice(13)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)} ${digits.slice(10, 13)} ${digits.slice(13, 16)}`;
    }

    if (country.flag === "gb") {
      if (digits.length <= 5) return digits;
      if (digits.length <= 8) return `${digits.slice(0, 5)} ${digits.slice(5)}`;
      if (digits.length <= 11)
        return `${digits.slice(0, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
      if (digits.length <= 14)
        return `${digits.slice(0, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)} ${digits.slice(11)}`;
      if (digits.length <= 16)
        return `${digits.slice(0, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)} ${digits.slice(11, 14)} ${digits.slice(14)}`;
      return `${digits.slice(0, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)} ${digits.slice(11, 14)} ${digits.slice(14, 16)}`;
    }

    // Generic formatting for other countries
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 9)
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    if (digits.length <= 12)
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
    if (digits.length <= 15)
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 12)} ${digits.slice(12)}`;
    if (digits.length <= 16)
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 12)} ${digits.slice(12, 15)} ${digits.slice(15)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 12)} ${digits.slice(12, 15)} ${digits.slice(15, 16)}`;
  }

  function handleKeyDown(event: KeyboardEvent) {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    const isDigit = /^[0-9]$/.test(event.key);
    const currentDigits = phoneNumber.replace(/[^0-9]/g, "");
    if (isDigit && currentDigits.length >= 16) {
      event.preventDefault();
      return;
    }

    if (!allowedKeys.includes(event.key) && !isDigit) {
      event.preventDefault();
    }
  }

  function handlePhoneChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const inputValue = target.value;

    const digitsOnly = inputValue.replace(/[^0-9]/g, "").slice(0, 16);

    const formattedNumber = formatPhoneNumber(digitsOnly, selectedCountry);
    phoneNumber = formattedNumber;

    // Clear validation when input is empty
    if (digitsOnly.length === 0) {
      isValid = true;
      dispatch("validationChange", {
        isValid: true,
        phoneData: {
          countryCode: selectedCountry.flag.toUpperCase(),
          phoneNumber: "",
          fullNumber: "",
          isValid: false,
        },
      });
    }
  }

  function handleBlur() {
    const digitsOnly = phoneNumber.replace(/\D/g, "");

    if (digitsOnly.length > 0) {
      const phoneData = validatePhoneNumber(digitsOnly, selectedCountry);
      isValid = phoneData.isValid;
      dispatch("validationChange", { isValid, phoneData });
    } else {
      isValid = true;
      dispatch("validationChange", {
        isValid: true,
        phoneData: {
          countryCode: selectedCountry.flag.toUpperCase(),
          phoneNumber: "",
          fullNumber: "",
          isValid: false,
        },
      });
    }
    dispatch("blur");
  }

  export function validate(): { isValid: boolean; phoneData: PhoneNumberData } {
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const phoneData = validatePhoneNumber(digitsOnly, selectedCountry);
    isValid = phoneData.isValid;
    dispatch("validationChange", { isValid, phoneData });

    return { isValid, phoneData };
  }

  function selectCountry(country: any) {
    selectedCountry = country;
    isOpen = false;

    if (phoneNumber) {
      const digitsOnly = phoneNumber.replace(/\D/g, "");
      const formattedNumber = formatPhoneNumber(digitsOnly, country);
      phoneNumber = formattedNumber;

      if (digitsOnly.length > 0) {
        const phoneData = validatePhoneNumber(digitsOnly, country);
        isValid = phoneData.isValid;
        dispatch("validationChange", { isValid, phoneData });
      }
    }

    dispatch("countryChange", { countryCode: country.code });
  }

  $: filteredCountries = COUNTRY_OPTIONS.filter((country) => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return false;

    const name = country.name.toLowerCase();
    const code = country.code.toLowerCase();

    return name.includes(query) || code.includes(query);
  }).sort((a, b) => {
    const query = searchQuery.toLowerCase().trim();
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();
    const aCode = a.code.toLowerCase();
    const bCode = b.code.toLowerCase();

    const aStartsWith = aName.startsWith(query) || aCode.startsWith(query);
    const bStartsWith = bName.startsWith(query) || bCode.startsWith(query);

    if (aStartsWith && !bStartsWith) return -1;
    if (bStartsWith && !aStartsWith) return 1;

    return aName.localeCompare(bName);
  });

  function handleClickOutside(event: MouseEvent) {
    if (
      dropdownContainer &&
      !dropdownContainer.contains(event.target as Node)
    ) {
      isOpen = false;
    }
  }

  function toggleDropdown() {
    isOpen = !isOpen;
  }

  onMount(() => {
    document?.addEventListener("click", handleClickOutside);

    // Dispatch initial country code
    dispatch("countryChange", { countryCode: selectedCountry.code });

    return () => {
      document?.removeEventListener("click", handleClickOutside);
    };
  });
</script>

<div
  bind:this={dropdownContainer}
  class="verbaflo-widget-phone-input-container"
>
  <div
    class="verbaflo-widget-phone-input-wrapper"
    class:error={showValidationError || (!isValid && phoneNumber.length > 0)}
  >
    <button
      type="button"
      on:click={toggleDropdown}
      class="verbaflo-widget-country-selector"
    >
      <FlagIcon code={selectedCountry.flag} />
      <span class="verbaflo-widget-country-code">{selectedCountry.code}</span>
      <ChevronDownIcon />
    </button>

    <div class="verbaflo-widget-vertical-separator"></div>

    <input
      class="verbaflo-widget-phone-number-input"
      type="text"
      placeholder="Phone Number"
      value={phoneNumber}
      on:keydown={handleKeyDown}
      on:input={handlePhoneChange}
      on:blur={handleBlur}
    />
  </div>

  {#if isOpen}
    <div class="verbaflo-widget-country-dropdown">
      <div class="verbaflo-widget-country-search-container">
        <SearchIcon />
        <input
          bind:value={searchQuery}
          placeholder="Search country or code"
          class="verbaflo-widget-country-search-input"
        />
      </div>

      <div class="verbaflo-widget-country-options-list">
        {#if searchQuery === ""}
          <div class="verbaflo-widget-country-section">
            <p class="verbaflo-widget-country-section-title">
              Popular Countries
            </p>
            <div class="verbaflo-widget-country-options">
              {#each popularCountries as country}
                <button
                  type="button"
                  on:click={() => selectCountry(country)}
                  class:selected={selectedCountry.name === country.name}
                  class="verbaflo-widget-country-option"
                >
                  <FlagIcon code={country.flag} />
                  <span class="verbaflo-widget-country-name"
                    >{country.name}</span
                  >
                  <span class="verbaflo-widget-country-code"
                    >{country.code}</span
                  >
                </button>
              {/each}
            </div>
          </div>
          <div class="verbaflo-widget-country-section">
            <p class="verbaflo-widget-country-section-title">All Countries</p>
            <div class="verbaflo-widget-country-options">
              {#each otherCountries as country}
                <button
                  type="button"
                  on:click={() => selectCountry(country)}
                  class:selected={selectedCountry.name === country.name}
                  class="verbaflo-widget-country-option"
                >
                  <FlagIcon code={country.flag} />
                  <span class="verbaflo-widget-country-name"
                    >{country.name}</span
                  >
                  <span class="verbaflo-widget-country-code"
                    >{country.code}</span
                  >
                </button>
              {/each}
            </div>
          </div>
        {:else}
          <div class="verbaflo-widget-country-section">
            <p class="verbaflo-widget-country-section-title">Search Results</p>
            <div class="verbaflo-widget-country-options">
              {#each filteredCountries as country}
                <button
                  type="button"
                  on:click={() => selectCountry(country)}
                  class:selected={selectedCountry.name === country.name}
                  class="verbaflo-widget-country-option"
                >
                  <FlagIcon code={country.flag} />
                  <span class="verbaflo-widget-country-name"
                    >{country.name}</span
                  >
                  <span class="verbaflo-widget-country-code"
                    >{country.code}</span
                  >
                </button>
              {/each}
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  :global(#vfwidget .verbaflo-widget-phone-input-container) {
    position: relative !important;
    width: 100% !important;
  }

  :global(#vfwidget .verbaflo-widget-phone-input-wrapper) {
    display: flex !important;
    align-items: center !important;
    border: 1px solid #d0d5dd !important;
    border-radius: 8px !important;
    background: white !important;
    overflow: hidden !important;
  }

  :global(#vfwidget .verbaflo-widget-phone-input-wrapper.error) {
    border: 1px solid #d80000 !important;
  }

  :global(#vfwidget .verbaflo-widget-country-selector) {
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
    padding: 8px !important;
    background: transparent !important;
    border: none !important;
    outline: none !important;
    cursor: pointer !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #374151 !important;
    min-width: fit-content !important;
    white-space: nowrap !important;
  }

  :global(#vfwidget .verbaflo-widget-country-selector:hover) {
    background-color: #f9fafb !important;
  }

  :global(#vfwidget .verbaflo-widget-vertical-separator) {
    width: 1px !important;
    height: 20px !important;
    background-color: #d0d5dd !important;
    margin: 0 2px !important;
  }

  :global(#vfwidget .verbaflo-widget-phone-number-input) {
    flex: 1 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    border: none !important;
    outline: none !important;
    padding: 10px !important;
    background: transparent !important;
    color: #374151 !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-widget-phone-number-input::placeholder) {
    color: #9ca3af !important;
  }

  :global(#vfwidget .verbaflo-widget-country-dropdown) {
    position: absolute !important;
    top: 100% !important;
    left: 0 !important;
    z-index: 1000 !important;
    background-color: #ffffff !important;
    border: 1px solid #d0d5dd !important;
    border-radius: 8px !important;
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
    margin-top: 4px !important;
    min-width: 200px !important;
    max-width: 300px !important;
  }

  :global(#vfwidget .verbaflo-widget-country-search-container) {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    padding: 8px !important;
    border-bottom: 1px solid #f3f4f6 !important;
  }

  :global(#vfwidget .verbaflo-widget-country-search-input) {
    width: 100% !important;
    border: none !important;
    outline: none !important;
    background: transparent !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #374151 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-widget-country-search-input::placeholder) {
    color: #9ca3af !important;
  }

  :global(#vfwidget .verbaflo-widget-country-options-list) {
    max-height: 200px !important;
    overflow-y: auto !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }

  :global(#vfwidget .verbaflo-widget-country-options-list::-webkit-scrollbar) {
    display: none !important;
  }

  :global(#vfwidget .verbaflo-widget-country-section) {
    padding: 8px 0 !important;
  }

  :global(#vfwidget .verbaflo-widget-country-section:not(:last-child)) {
    border-bottom: 1px solid #f3f4f6 !important;
  }

  :global(#vfwidget .verbaflo-widget-country-section-title) {
    font-size: 12px !important;
    font-weight: 500 !important;
    color: #6b7280 !important;
    margin: 0 0 4px 8px !important;
    padding: 0 !important;
    text-transform: none !important;
  }

  :global(#vfwidget .verbaflo-widget-country-options) {
    display: flex !important;
    flex-direction: column !important;
  }

  :global(#vfwidget .verbaflo-widget-country-option) {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    padding: 8px 12px !important;
    background: transparent !important;
    border: none !important;
    outline: none !important;
    cursor: pointer !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: #374151 !important;
    text-align: left !important;
    width: 100% !important;
    transition: background-color 0.15s ease !important;
  }

  :global(#vfwidget .verbaflo-widget-country-option:hover) {
    background-color: #f9fafb !important;
  }

  :global(#vfwidget .verbaflo-widget-country-option.selected) {
    background-color: #f3f4f6 !important;
  }

  :global(#vfwidget .verbaflo-widget-country-name) {
    flex: 1 !important;
    font-size: 14px !important;
    color: #374151 !important;
  }

  :global(#vfwidget .verbaflo-widget-country-code) {
    font-size: 14px !important;
    color: #6b7280 !important;
    font-weight: 500 !important;
  }
</style>
